import { useState, useEffect } from 'react';
import { useCartStore } from '../stores/cartStore'
import { useSpring, animated } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { CheckoutModal } from './CheckoutModal';
import { useTheme } from '../hooks/useTheme'

const ShoppingCart = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const cartStore = useCartStore();
  const theme = useTheme(state => state.getThemeStyles())

  const [{ x }, api] = useSpring(() => ({ 
    x: 100,
    config: { tension: 200, friction: 25 }
  }));

  useEffect(() => {
    api.start({ x: isOpen ? 0 : 100 });
  }, [isOpen]);

  const bind = useDrag(({ movement: [mx], down, velocity: [vx], cancel }) => {
    if (mx < 0) {
      cancel();
      return;
    }

    if (down) {
      const percent = (mx / window.innerWidth) * 100;
      api.start({ x: percent, immediate: true });
    } else {
      if (mx > window.innerWidth * 0.3 || vx > 0.5) {
        setIsOpen(false);
      }
      api.start({ x: isOpen ? 0 : 100 });
    }
  }, {
    axis: 'x',
    bounds: { left: 0, right: window.innerWidth * 0.3 },
    rubberband: true,
    enabled: isOpen
  });

  const getTotalItems = () => {
    return cartStore.items.reduce((total, item) => total + item.quantity, 0);
  };

  const handleCheckout = () => {
    setIsOpen(false);
    setIsCheckoutOpen(true);
  };

  return (
    <>
      <button
        id="cart-button"
        onClick={() => setIsOpen(true)}
        className={theme.components.cart.button.container}
        aria-label="Abrir carrinho"
      >
        <div className={theme.components.cart.button.wrapper}>
          <div className={theme.components.cart.button.hexagon.outer} />
          <div className={theme.components.cart.button.hexagon.middle} />
          <div className={theme.components.cart.button.hexagon.inner}>
            <div 
              className={theme.components.cart.button.icon.className}
              dangerouslySetInnerHTML={{ __html: theme.components.cart.button.icon.svg }}
            />
          </div>

          {cartStore.items.length > 0 && (
            <div className={theme.components.cart.button.counter.wrapper}>
              <div className={theme.components.cart.button.counter.container}>
                <div className={theme.components.cart.button.counter.background} />
                <span className={theme.components.cart.button.counter.text}>
                  {getTotalItems()}
                </span>
              </div>
            </div>
          )}
        </div>
      </button>

      <div 
        className={`${theme.components.cart.panel.overlay} ${isOpen ? 'opacity-100' : 'opacity-0'}`}
        style={{ 
          zIndex: isOpen ? 55 : -1,
          pointerEvents: isOpen ? 'auto' : 'none' 
        }}
        onClick={() => setIsOpen(false)}
      />

      <animated.div
        {...bind()}
        style={{
          transform: x.to(value => `translateX(${value}%)`),
          touchAction: 'pan-y',
          zIndex: isOpen ? 60 : -1
        }}
        className={`${theme.components.cart.panel.container} ${theme.effects.gradient.surface} 
          ${theme.colors.border.primary}`}>
        
        {/* Cabeçalho */}
        <div className={theme.components.cart.panel.header.wrapper}>
          <div className={theme.components.cart.panel.header.row}>
            <h2 className={theme.components.cart.panel.header.title}>
              Carrinho
            </h2>
            <button
              onClick={() => setIsOpen(false)}
              className={theme.components.cart.panel.header.closeButton.container}
              aria-label="Fechar carrinho"
            >
              <div className={theme.components.cart.panel.header.closeButton.background} />
              <div className={`${theme.components.cart.panel.header.closeButton.wrapper}
                ${theme.components.cart.panel.header.closeButton.gradients.before}
                ${theme.components.cart.panel.header.closeButton.gradients.after}`}>
                <svg className={theme.components.cart.panel.header.closeButton.icon}
                  viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </button>
          </div>
        </div>

        {/* Lista de Itens */}
        <div className={theme.components.cart.panel.content.wrapper}>
          {cartStore.items.map((item, index) => (
            <div key={item.id} className={theme.components.cart.panel.content.item.container}>
              {/* Linha 1: Título + Remover */}
              <div className={theme.components.cart.panel.content.item.row.wrapper}>
                <h3 className={theme.components.cart.panel.content.item.title}>
                  #{index + 1} - {item.fileName}
                </h3>
                <button
                  onClick={() => cartStore.removeItem(item.id)}
                  className={theme.components.cart.panel.content.item.removeButton.container}
                  aria-label="Remover item"
                >
                  <div className={theme.components.cart.panel.content.item.removeButton.background} />
                  <svg
                    className={theme.components.cart.panel.content.item.removeButton.icon}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Linha 2: Quantidade + Valor */}
              <div className={theme.components.cart.panel.content.item.controls.quantity.wrapper}>
                <button
                  onClick={() => cartStore.decrementQuantity(item.id)}
                  className={theme.components.cart.panel.content.item.controls.quantity.button}
                  aria-label="Diminuir quantidade"
                >
                  <svg 
                    className={theme.components.cart.panel.content.item.controls.quantity.icon}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4"/>
                  </svg>
                </button>
                
                <span className={theme.components.cart.panel.content.item.controls.quantity.text}>
                  {item.quantity}
                </span>
                
                <button
                  onClick={() => cartStore.incrementQuantity(item.id)}
                  className={theme.components.cart.panel.content.item.controls.quantity.button}
                  aria-label="Aumentar quantidade"
                >
                  <svg 
                    className={theme.components.cart.panel.content.item.controls.quantity.icon}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Rodapé */}
        <div className={theme.components.cart.panel.footer.container}>
          <div className={theme.components.cart.panel.footer.row}>
            <span className={theme.components.cart.panel.footer.total.label}>
              Total:
            </span>
            <span className={theme.components.cart.panel.footer.total.value}>
              R$ {cartStore.totalAmount.toFixed(2)}
            </span>
          </div>
          
          <button 
            disabled={!cartStore.items.length}
            className={`${theme.components.cart.panel.footer.button.base} 
              ${cartStore.items.length 
                ? theme.components.cart.panel.footer.button.enabled
                : theme.components.cart.panel.footer.button.disabled
              }`}
            onClick={() => {
              if (cartStore.items.length) {
                setIsOpen(false);
                handleCheckout();
              }
            }}
          >
            <span className={`relative text-lg font-medium transition-all duration-300
              ${cartStore.items.length
                ? theme.components.cart.panel.footer.button.text.enabled
                : theme.components.cart.panel.footer.button.text.disabled
              }`}
            >
              Enviar Pedido
            </span>
          </button>
        </div>
      </animated.div>

      <CheckoutModal 
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
      />
    </>
  );
};

export default ShoppingCart;